import { Button, Col, Form, Input, Modal, Row } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

import { DocumentLinkEntry, documentLinkErrors } from "../../Constants";
import { blankLink } from "../../Onboarding/Steps/ProjectDocuments";
import styles from "../../WorkOrder.module.scss";

interface Props {
  open: boolean;
  /** The links as the work order holds them. */
  links: DocumentLinkEntry[];
  saving?: boolean;
  onCancel: () => void;
  onSave: (links: DocumentLinkEntry[]) => void;
}

/**
 * "Edit Additional Document Link", as e-POD's detail page has it: name/link
 * rows, a bin per row, "Add File Link", Cancel / Save. A row needs both its
 * name and its link; a repeated or broken one is refused, as in the wizard.
 */
export const EditLinksModal = ({ open, links, saving, onCancel, onSave }: Props) => {
  const [rows, setRows] = useState<DocumentLinkEntry[]>([]);

  useEffect(() => {
    if (open) setRows(links.length ? links : [blankLink(0)]);
  }, [open, links]);

  const shared = documentLinkErrors(rows);
  const errorsOf = (row: DocumentLinkEntry) => {
    const name = String(row.documentName ?? "").trim();
    const link = String(row.documentLink ?? "").trim();
    return {
      name: shared[row.key]?.name ?? (!name && link ? "Document Name is required" : undefined),
      link: shared[row.key]?.link ?? (name && !link ? "Document Link is required" : undefined),
    };
  };
  const hasErrors = rows.some((row) => errorsOf(row).name || errorsOf(row).link);
  const complete = rows.every((row) => String(row.documentName).trim() && String(row.documentLink).trim());

  const update = (key: string, patch: Partial<DocumentLinkEntry>) =>
    setRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  return (
    <Modal
      title="Edit Additional Document Link"
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      closable={false}
      centered
      footer={[
        <Button
          key="cancel"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={hasErrors}
          onClick={() => onSave(rows)}
        >
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical">
        {rows.map((row) => {
          const errors = errorsOf(row);
          return (
            <Row
              key={row.key}
              gutter={16}
              align="top"
            >
              <Col span={10}>
                <Form.Item
                  validateStatus={errors.name ? "error" : undefined}
                  help={errors.name}
                >
                  <Input
                    placeholder="Document Name"
                    value={row.documentName}
                    onChange={(e) => update(row.key, { documentName: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={11}>
                <Form.Item
                  validateStatus={errors.link ? "error" : undefined}
                  help={errors.link}
                >
                  <Input
                    placeholder="Document Link"
                    value={row.documentLink}
                    onChange={(e) => update(row.key, { documentLink: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Button
                  type="link"
                  danger
                  aria-label="Remove link"
                  disabled={rows.length === 1}
                  onClick={() => setRows(rows.filter((other) => other.key !== row.key))}
                >
                  <Icon icon="fluent:delete-16-regular" />
                </Button>
              </Col>
            </Row>
          );
        })}
        <Row justify="end">
          <span
            className={complete && !hasErrors ? styles.addAnother : styles.addAnotherDisabled}
            onClick={() => complete && !hasErrors && setRows([...rows, blankLink(Date.now())])}
          >
            <Icon
              icon="akar-icons:plus"
              inline
            />
            Add File Link
          </span>
        </Row>
      </Form>
    </Modal>
  );
};
